import { useQueryClient } from "@tanstack/react-query";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import { useUsersMe } from "@/entities/users/hooks";
import { invalidateReportsList } from "@/entities/savedReports/queryKeys";
import {
  SCHEDULER_FILTER_KEY,
  SCHEDULER_ITEM_KEY,
  SCHEDULER_LIST_KEY
} from "@/entities/reportingScheduler/queryKeys";
import {
  CreateReportingSchedulerRequest,
  ReportingScheduler,
  UpdateReportingSchedulerRequest
} from "@/entities/reportingScheduler/types";


const invalidateScheduler = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({ queryKey: [SCHEDULER_ITEM_KEY] });
  queryClient.invalidateQueries({ queryKey: [SCHEDULER_LIST_KEY] });
  queryClient.invalidateQueries({ queryKey: [SCHEDULER_FILTER_KEY] });
  // A report row carries its own schedule, so every schedule change dates the
  // reports list: left alone, a row keeps offering to remove a schedule that is
  // already gone, and the scheduled filter keeps listing it.
  // Scheduling or removing a report also moves the Reports tab badge.
  invalidateReportsList(queryClient);
};

export const useReportingSchedulers = (
  name?: string
): ExtendedUseQueryResult<ReportingScheduler[]> =>
  useAPI(SCHEDULER_LIST_KEY, {
    queryParams: name ? { name } : undefined
  });

export const useReportingScheduler = (
  id: string | null | undefined
): ExtendedUseQueryResult<ReportingScheduler | null> =>
  useAPI(
    SCHEDULER_ITEM_KEY,
    { pathParams: id ? { id } : undefined },
    {
      enabled: !!id,
      select: (data: ReportingScheduler | undefined) =>
        data && !data.isDeleted ? data : null
    }
  );

export const useReportingSchedulersFilter = (
  enabled = true,
  searchTerm?: string,
  sortField?: string,
  sortAsc?: boolean
): ExtendedUseQueryResult<ReportingScheduler[]> => {
  const { data: me } = useUsersMe();
  const userId = me?.id;
  const trimmedSearch = searchTerm?.trim() || undefined;
  return useAPI(
    SCHEDULER_FILTER_KEY,
    {
      method: "POST",
      data: {
        filters: {
          userId,
          isDeleted: false
        },
        sort: sortField
          ? { field: sortField, direction: sortAsc ? "asc" : "desc" }
          : { field: "createdAt", direction: "desc" },
        ...(trimmedSearch ? { search: { term: trimmedSearch } } : {})
      }
    },
    {
      enabled: enabled && !!userId,
      select: (
        data: { reports_schedules?: ReportingScheduler[] } | undefined
      ) => data?.reports_schedules ?? []
    }
  );
};

export const useCreateReportingScheduler = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    { data: CreateReportingSchedulerRequest },
    ReportingScheduler
  >(SCHEDULER_LIST_KEY, { method: "POST" }, () =>
    invalidateScheduler(queryClient)
  );
};

export const useUpdateReportingScheduler = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    {
      pathParams: { id: string };
      data: UpdateReportingSchedulerRequest;
    },
    ReportingScheduler
  >(SCHEDULER_ITEM_KEY, { method: "PUT" }, () =>
    invalidateScheduler(queryClient)
  );
};

export const useDeleteReportingScheduler = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<{ pathParams: { id: string } }, void>(
    SCHEDULER_ITEM_KEY,
    { method: "DELETE" },
    (_response, variables) => {
      const deletedId = variables.pathParams.id;
      queryClient.setQueriesData<ReportingScheduler | undefined>(
        { queryKey: [SCHEDULER_ITEM_KEY] },
        (old) =>
          old && old.id === deletedId ? { ...old, isDeleted: true } : old
      );
      invalidateScheduler(queryClient);
    }
  );
};
